import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient, TICKET_STATUS_LABELS, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LinkSimple, MagnifyingGlass, ArrowRight, Package, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const REQUEST_STATUS_LABELS = {
  unassigned: "Non prise en charge",
  processing: "En cours de traitement",
  completed: "Traitée",
  archived: "Archivée",
};

// Palette éco — styles de badges
const CHIP = {
  neutral: "bg-[#F2F1EC] text-[#68706A] border-[#E4E2DA]",
  blue: "bg-[#EEF3FB] text-[#3E5AA8] border-[#D6E0F2]",
  orange: "bg-[#FFF9F0] text-[#B96B15] border-[#FCE3C5]",
  green: "bg-[#ECF9F1] text-[#218A49] border-[#C7EED8]",
  red: "bg-[#FDF2F2] text-[#C83B3B] border-[#F6D5D5]",
  violet: "bg-[#F5F1FB] text-[#7A5AA8] border-[#E4D9F3]",
};
const DOT = {
  neutral: "bg-[#A8A399]",
  blue: "bg-[#3E5AA8]",
  orange: "bg-[#B96B15]",
  green: "bg-[#218A49]",
  red: "bg-[#C83B3B]",
  violet: "bg-[#7A5AA8]",
};

const TASK_CHIP = {
  a_demonter: "neutral", pris_en_charge: "blue", en_cours: "orange",
  demontee: "green", controlee: "green", probleme: "red",
};
const TICKET_CHIP = {
  new: "blue", unassigned: "red", in_progress: "orange",
  waiting_client: "violet", resolved: "green", closed: "neutral",
};
const REQUEST_CHIP = {
  unassigned: "red", processing: "orange", completed: "green", archived: "neutral",
};

function Chip({ tone, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium border shrink-0 ${CHIP[tone]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${DOT[tone]}`} />
      {children}
    </span>
  );
}

export default function PortalTrack() {
  const navigate = useNavigate();

  const [txnInput, setTxnInput] = useState("");
  const [linkInput, setLinkInput] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Extrait un token de suivi depuis un lien complet OU un token collé seul
  const extractToken = (raw) => {
    const url = raw.match(/suivi\/([A-Za-z0-9_-]{10,})/);
    if (url) return url[1];
    const bare = raw.match(/^([A-Za-z0-9_-]{10,})$/);
    return bare ? bare[1] : null;
  };

  const searchByTxn = async (e) => {
    e.preventDefault();
    const q = txnInput.trim();
    if (!q || q.length < 3) { toast.error("Saisissez un numéro de transaction (min. 3 caractères)"); return; }
    setLoading(true);
    setResults(null);
    try {
      const { data } = await apiClient.get(`/portal/order/${encodeURIComponent(q)}`);
      setResults(data);
    } catch (err) {
      console.warn("track failed", err);
      toast.error("Erreur lors de la recherche");
    } finally { setLoading(false); }
  };

  const goByLink = (e) => {
    e.preventDefault();
    const raw = linkInput.trim();
    if (!raw) { toast.error("Saisissez votre lien de suivi"); return; }
    const token = extractToken(raw);
    if (!token) { toast.error("Lien de suivi invalide"); return; }
    navigate(`/portail/suivi/${token}`);
  };

  const inputCls = "h-14 rounded-xl bg-white border-neutral-200 px-4 text-base focus-visible:ring-2 focus-visible:ring-[#24593C]/25 focus-visible:border-[#24593C] focus-visible:ring-offset-0";

  return (
    <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 portal-fade-up" data-testid="portal-track-page">
      <Link to="/portail" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#68706A] hover:text-[#24593C] transition-colors mb-5">
        <ArrowLeft size={16} weight="bold" /> Retour
      </Link>

      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D211F] mb-2">Suivi de votre demande</h1>
      <p className="text-base text-[#68706A] mb-6">Recherchez par numéro de transaction, ou accédez directement à votre ticket via le lien reçu par e-mail.</p>

      <div className="space-y-4">
        <TxnOption input={txnInput} setInput={setTxnInput} onSubmit={searchByTxn} loading={loading} inputCls={inputCls} />
        <Divider />
        <LinkOption input={linkInput} setInput={setLinkInput} onSubmit={goByLink} inputCls={inputCls} />
      </div>

      {results && (
        <div className="mt-5 portal-fade-up" data-testid="order-results">
          {results.count === 0 ? (
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] px-4 py-8 text-center" data-testid="order-empty">
              <div className="w-14 h-14 rounded-2xl bg-[#F7F6F2] flex items-center justify-center mx-auto mb-3">
                <Package size={28} weight="duotone" className="text-[#A8A399]" />
              </div>
              <p className="text-[15px] text-[#1D211F]">Aucune demande trouvée pour la transaction <span className="font-mono font-semibold">{results.transaction_number}</span>.</p>
              <div className="mt-1.5 text-sm text-[#68706A]">Vérifiez votre numéro ou <Link to="/portail/nouveau" className="text-[#24593C] font-medium underline underline-offset-2">ouvrez un nouveau ticket</Link>.</div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6 space-y-5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#68706A]">{results.count} élément{results.count > 1 ? "s" : ""} trouvé{results.count > 1 ? "s" : ""}</span>
                <span className="font-mono font-medium text-[#1D211F] bg-[#F7F6F2] rounded-full px-3 py-1 border border-black/[0.05]">TRX {results.transaction_number}</span>
              </div>
              {(results.workshop_tasks || []).length > 0 && (
                <ResultGroup title="Pièces en préparation (atelier)" testId="workshop-tasks">
                  {results.workshop_tasks.map((t) => <TaskRow key={t.id} t={t} />)}
                </ResultGroup>
              )}
              {(results.tickets || []).length > 0 && (
                <ResultGroup title="Tickets associés" testId="linked-tickets">
                  {results.tickets.map((t) => <TicketRow key={t.reference} t={t} />)}
                </ResultGroup>
              )}
              {(results.requests || []).length > 0 && (
                <ResultGroup title="Traitements internes" testId="internal-requests">
                  {results.requests.map((r) => <OrderRow key={r.id} r={r} />)}
                </ResultGroup>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 px-2" aria-hidden="true">
      <span className="flex-1 h-px bg-neutral-200" />
      <span className="text-xs font-medium uppercase tracking-wider text-[#A8A399]">ou</span>
      <span className="flex-1 h-px bg-neutral-200" />
    </div>
  );
}

function TxnOption({ input, setInput, onSubmit, loading, inputCls }) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6" data-testid="track-order-panel">
      <label className="flex items-center gap-2 text-sm font-medium text-[#1D211F] mb-2">
        <MagnifyingGlass size={16} weight="bold" className="text-[#24593C]" /> Par numéro de transaction
      </label>
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Ex: TRX-2026-0001"
        className={`${inputCls} font-mono`}
        data-testid="txn-input"
      />
      <p className="text-xs text-[#68706A] mt-2">Vous verrez l&apos;état d&apos;avancement dans chaque service concerné (Comptabilité, SAV, Expédition, Atelier…).</p>
      <Button type="submit" disabled={loading} className="mt-4 w-full sm:w-auto bg-[#24593C] hover:bg-[#1B432D] text-white rounded-full h-14 px-6 text-base font-medium gap-2 active:scale-[0.98] transition-all" data-testid="submit-track-order">
        <MagnifyingGlass size={18} weight="bold" /> {loading ? "Recherche..." : "Rechercher"}
      </Button>
    </form>
  );
}

function LinkOption({ input, setInput, onSubmit, inputCls }) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6" data-testid="track-ticket-panel">
      <label className="flex items-center gap-2 text-sm font-medium text-[#1D211F] mb-2">
        <LinkSimple size={16} weight="bold" className="text-[#24593C]" /> Par lien de suivi
      </label>
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Collez le lien reçu par e-mail"
        className={`${inputCls} font-mono text-sm`}
        data-testid="tracking-input"
      />
      <p className="text-xs text-[#68706A] mt-2">Le lien de suivi vous a été envoyé par e-mail à l&apos;ouverture de votre ticket.</p>
      <Button type="submit" className="mt-4 w-full sm:w-auto bg-[#24593C] hover:bg-[#1B432D] text-white rounded-full h-14 px-6 text-base font-medium gap-2 active:scale-[0.98] transition-all" data-testid="goto-ticket">
        Accéder au ticket <ArrowRight size={18} weight="bold" />
      </Button>
    </form>
  );
}

function ResultGroup({ title, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-[#68706A] font-medium mb-2 px-1">{title}</div>
      <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden bg-white">
        {children}
      </div>
    </div>
  );
}

/* Timeline responsive : verticale sur mobile, horizontale sur desktop */
function Timeline({ steps, tone = "green" }) {
  const lastDone = steps.reduce((acc, s, i) => (s.done ? i : acc), 0);
  const doneBar = "bg-[#24593C]";
  const doneDot = "bg-[#24593C] border-[#24593C]";
  return (
    <div className="mt-3">
      {/* Mobile — vertical */}
      <ol className="md:hidden relative" data-testid="timeline">
        {steps.map((s, i) => (
          <li key={s.label} className="relative pl-7 pb-4 last:pb-0">
            {i < steps.length - 1 && (
              <span className={`absolute left-[5px] top-3 -bottom-1 w-[2px] ${steps[i + 1].done ? doneBar : "bg-neutral-200"}`} />
            )}
            <span className={`absolute left-0 top-1 w-3 h-3 rounded-full border-2 ${s.done ? doneDot : "bg-white border-neutral-300"} ${s.done && i === lastDone ? "ring-4 ring-[#24593C]/15" : ""}`} />
            <div className={`text-sm leading-tight ${s.done ? "text-[#1D211F] font-medium" : "text-neutral-400"}`}>{s.label}</div>
            {s.date && <div className="text-[11px] text-neutral-400 font-mono mt-0.5">{formatDate(s.date)}</div>}
          </li>
        ))}
      </ol>
      {/* Desktop — horizontal */}
      <div className="hidden md:flex items-start pr-2">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-start flex-1 last:flex-none">
            <div className="flex flex-col items-center shrink-0 w-[84px]">
              <span className={`w-3 h-3 rounded-full border-2 ${s.done ? doneDot : "bg-white border-neutral-300"} ${s.done && i === lastDone ? "ring-4 ring-[#24593C]/15" : ""}`} />
              <div className={`text-[11px] mt-1.5 text-center leading-tight ${s.done ? "text-[#1D211F] font-medium" : "text-neutral-400"}`}>{s.label}</div>
              {s.date && <div className="text-[10px] text-neutral-400 font-mono text-center">{formatDate(s.date)}</div>}
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-[2px] mt-[5px] min-w-[16px] ${steps[i + 1].done ? doneBar : "bg-neutral-200"}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketRow({ t }) {
  const tone = TICKET_CHIP[t.status] || "blue";
  const takenDone = ["in_progress", "waiting_client", "resolved", "closed"].includes(t.status);
  const steps = [
    { label: "Ouvert", date: t.created_at, done: true },
    { label: "Pris en charge", date: null, done: takenDone },
    { label: "Résolu", date: t.resolved_at, done: !!t.resolved_at || ["resolved", "closed"].includes(t.status) },
    { label: "Clôturé", date: t.closed_at, done: !!t.closed_at || t.status === "closed" },
  ];
  return (
    <div className="px-4 py-4" data-testid={`ticket-row-${t.reference}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-[#1D211F]">
            <span className="font-mono text-[#68706A] mr-2">{t.reference}</span>{t.subject}
          </div>
          <div className="text-xs text-[#68706A] mt-0.5">Pour répondre, utilisez le lien sécurisé reçu par e-mail.</div>
        </div>
        <Chip tone={tone}>{TICKET_STATUS_LABELS[t.status] || t.status}</Chip>
      </div>
      <Timeline steps={steps} />
    </div>
  );
}

function TaskRow({ t }) {
  const tone = TASK_CHIP[t.status] || "neutral";
  const order = ["a_demonter", "pris_en_charge", "en_cours", "demontee", "controlee"];
  const idx = order.indexOf(t.status);
  const steps = [
    { label: "Reçue", date: t.created_at, done: true },
    { label: "Prise en charge", date: t.taken_at, done: !!t.taken_at || idx >= 1 },
    { label: "En cours", date: t.started_at, done: !!t.started_at || idx >= 2 },
    { label: "Démontée", date: t.demontee_at, done: !!t.demontee_at || idx >= 3 },
    { label: "Contrôlée", date: t.controlee_at, done: !!t.controlee_at || idx >= 4 },
  ];
  return (
    <div className="px-4 py-4" data-testid={`task-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-[#1D211F]">{t.designation}</div>
          {t.vehicle && <div className="text-xs text-[#68706A] mt-0.5 uppercase tracking-wide">{t.vehicle}</div>}
        </div>
        <Chip tone={tone}>{t.status_label}</Chip>
      </div>
      {t.status === "probleme" ? (
        <div className="mt-2.5 flex items-start gap-2 text-[13px] text-[#C83B3B] bg-[#FDF2F2] border border-[#F6D5D5] rounded-lg px-3 py-2" data-testid="task-problem-note">
          <WarningCircle size={16} weight="duotone" className="shrink-0 mt-0.5" />
          Un problème a été signalé sur cette pièce. Notre équipe vous recontactera rapidement.
        </div>
      ) : (
        <Timeline steps={steps} />
      )}
    </div>
  );
}

function OrderRow({ r }) {
  const tone = REQUEST_CHIP[r.status] || "red";
  const idx = ["unassigned", "processing", "completed", "archived"].indexOf(r.status);
  const steps = [
    { label: "Reçue", date: r.created_at, done: true },
    { label: "Prise en charge", date: r.taken_at, done: !!r.taken_at || idx >= 1 },
    { label: "Traitée", date: r.completed_at, done: !!r.completed_at || idx >= 2 },
  ];
  return (
    <div className="px-4 py-4" data-testid={`order-row-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-[#1D211F]">
            {r.sender_pole_label} <span className="text-neutral-400 mx-1">→</span> {r.receiver_pole_label}
          </div>
          {r.taken_by_name && (
            <div className="text-xs text-[#68706A] mt-0.5">Prise en charge par <span className="font-medium text-[#1D211F]">{r.taken_by_name}</span></div>
          )}
        </div>
        <Chip tone={tone}>{REQUEST_STATUS_LABELS[r.status] || r.status}</Chip>
      </div>
      <Timeline steps={steps} />
    </div>
  );
}
